import { Point, RetroTemplate, TemplateZone, ZoneShape } from './types';

/** Top-left corner of the template bounding box on the board. */
export function templateOrigin(template: RetroTemplate): Point {
  return {
    x: template.anchor.x - template.size.width / 2,
    y: template.anchor.y - template.size.height / 2,
  };
}

function toBoardPoint(local: Point, template: RetroTemplate): Point {
  const origin = templateOrigin(template);
  return { x: origin.x + local.x, y: origin.y + local.y };
}

function containsLocalPoint(point: Point, shape: ZoneShape, template: RetroTemplate): boolean {
  const origin = templateOrigin(template);
  const local = { x: point.x - origin.x, y: point.y - origin.y };

  switch (shape.kind) {
    case 'rect':
      return (
        local.x >= shape.x
        && local.y >= shape.y
        && local.x < shape.x + shape.width
        && local.y < shape.y + shape.height
      );
    case 'circle': {
      const dx = local.x - shape.cx;
      const dy = local.y - shape.cy;
      const rx = shape.radius;
      const ry = shape.radiusY ?? rx;
      // Normalise so the outer boundary is at distance 1 — works for circles
      // (rx === ry) and ellipses alike.
      const normDist = Math.hypot(dx / rx, dy / ry);
      const minRatio = (shape.minRadius ?? 0) / rx;
      return normDist >= minRatio && normDist <= 1;
    }
    case 'polygon':
      return pointInPolygon(local, shape.points);
    default:
      return false;
  }
}

/** Ray-casting point-in-polygon test.
 * Horizontal edges (yi === yj) are naturally skipped because the condition
 * `(yi > point.y) !== (yj > point.y)` is always false when yi === yj,
 * so no division by (yj - yi) occurs for those edges. */
function pointInPolygon(point: Point, polygon: Point[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;
    const intersect = ((yi > point.y) !== (yj > point.y))
      && (point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * Returns the zone whose shape contains the point, or null if outside all zones.
 * For overlapping zones (e.g. nested circles), prefers the smallest area match.
 */
export function findZoneForPoint(point: Point, template: RetroTemplate): TemplateZone | null {
  const matches = template.zones.filter(z => containsLocalPoint(point, z.shape, template));
  if (matches.length === 0) return null;
  if (matches.length === 1) return matches[0];

  // Smallest area wins — inner circle before outer ring.
  matches.sort((a, b) => zoneArea(a) - zoneArea(b));
  return matches[0];
}

function zoneArea(zone: TemplateZone): number {
  const { shape } = zone;
  if (shape.kind === 'rect') return shape.width * shape.height;
  if (shape.kind === 'polygon') {
    // Shoelace formula
    let area = 0;
    for (let i = 0, j = shape.points.length - 1; i < shape.points.length; j = i++) {
      area += (shape.points[j].x + shape.points[i].x) * (shape.points[j].y - shape.points[i].y);
    }
    return Math.abs(area / 2);
  }
  const rx = shape.radius;
  const ry = shape.radiusY ?? rx;
  const minR = shape.minRadius ?? 0;
  const outer = Math.PI * rx * ry;
  // Inner boundary is a same-aspect-ratio scaled-down ellipse.
  const inner = minR > 0 ? Math.PI * minR * minR * (ry / rx) : 0;
  return outer - inner;
}

/** Centre of a zone's bounding box in board coordinates (for viewport fit). */
export function zoneBoardCentre(zone: TemplateZone, template: RetroTemplate): Point {
  if (zone.shape.kind === 'rect') {
    return toBoardPoint(
      { x: zone.shape.x + zone.shape.width / 2, y: zone.shape.y + zone.shape.height / 2 },
      template,
    );
  }
  if (zone.shape.kind === 'polygon' && zone.shape.points.length > 0) {
    const cx = zone.shape.points.reduce((s, p) => s + p.x, 0) / zone.shape.points.length;
    const cy = zone.shape.points.reduce((s, p) => s + p.y, 0) / zone.shape.points.length;
    return toBoardPoint({ x: cx, y: cy }, template);
  }
  if (zone.shape.kind === 'circle') {
    return toBoardPoint({ x: zone.shape.cx, y: zone.shape.cy }, template);
  }
  return toBoardPoint({ x: 0, y: 0 }, template);
}

/** Board-space bounding box covering the entire template. */
export function templateBoardBounds(template: RetroTemplate): {
  minX: number; minY: number; maxX: number; maxY: number;
} {
  const origin = templateOrigin(template);
  return {
    minX: origin.x,
    minY: origin.y,
    maxX: origin.x + template.size.width,
    maxY: origin.y + template.size.height,
  };
}
